import { type KeyboardEvent, useMemo } from 'react';
import { FaChevronRight } from 'react-icons/fa';
import StatusBadge from '@/components/UI/StatusBadge';
import Tooltip from '@/components/UI/Tooltip';
import { type AlphaAtlasReportRow, EVIDENCE_STATE_META, summarizeEntityBreakdown } from './AlphaAtlasReportState';
import { formatCount, formatPercentMicros, formatRatio, TONE_TEXT_CLASS, toneForMicros } from './alphaAtlasFormat';

interface AlphaAtlasEntityBreakdownViewProps {
    /** The pooled row being broken down */
    pooledRow: AlphaAtlasReportRow;
    /** Entity-scope rows for the same hypothesis, direction, horizon, and window */
    rows: AlphaAtlasReportRow[];
    /** Total entity rows on the server (the page may be capped) */
    total: number;
    onSelectRow: (row: AlphaAtlasReportRow) => void;
}

const TH = 'px-2 py-1.5 text-xs font-medium uppercase tracking-wider text-zinc-400 whitespace-nowrap';
const TD = 'px-2 py-1.5 align-top text-sm tabular-nums whitespace-nowrap';

const Signed = ({ micros, className = '' }: { micros: number | null; className?: string }) => (
    <span className={`${TONE_TEXT_CLASS[toneForMicros(micros)]} ${className}`}>{formatPercentMicros(micros, { signed: true })}</span>
);

/** Which entities carry a pooled result: each entity's own numbers for the same hypothesis, largest support first after edge. */
const AlphaAtlasEntityBreakdownView = ({ pooledRow, rows, total, onSelectRow }: AlphaAtlasEntityBreakdownViewProps) => {
    const contributing = useMemo(() => rows.filter((row) => row.supportCount > 0), [rows]);
    const summary = useMemo(() => summarizeEntityBreakdown(rows), [rows]);
    const showLift = pooledRow.conditionKind === 'parentAndChild';

    const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, row: AlphaAtlasReportRow) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelectRow(row);
        }
    };

    if (contributing.length === 0) {
        return <p className="text-sm text-zinc-500">No entity contributed observations to this result in this window.</p>;
    }

    return (
        <div className="space-y-2">
            <p className="text-sm text-zinc-300">
                <span className="font-medium text-zinc-100">{formatCount(summary.entitiesWithObservations)}</span> of {formatCount(total)}{' '}
                entities contributed observations ·{' '}
                <span className="font-medium text-zinc-100">{formatCount(summary.positiveEdgeEntities)}</span> beat random entry on their
                own · <span className="font-medium text-zinc-100">{formatCount(summary.sufficientSupportEntities)}</span> with sufficient
                support
                {summary.topEntityLabel && summary.topEntitySupportShare !== null && (
                    <>
                        {' '}
                        · largest contributor <span className="font-medium text-zinc-100">{summary.topEntityLabel}</span> (
                        {formatRatio(summary.topEntitySupportShare, 0)} of observations)
                    </>
                )}
            </p>
            <div className="max-h-80 overflow-auto rounded-lg border border-zinc-800">
                <table className="w-full text-left">
                    <thead className="sticky top-0 bg-[#131416]">
                        <tr>
                            <th className={TH} scope="col">
                                Entity
                            </th>
                            <th className={`${TH} text-right`} scope="col">
                                Support
                            </th>
                            <th className={`${TH} text-right`} scope="col">
                                Net
                            </th>
                            <th className={`${TH} text-right`} scope="col">
                                Edge vs random
                            </th>
                            {showLift && (
                                <th className={`${TH} text-right`} scope="col">
                                    Lift
                                </th>
                            )}
                            <th className={TH} scope="col">
                                Evidence
                            </th>
                            <th className={TH} scope="col">
                                <span className="sr-only">Open</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                        {contributing.map((row) => (
                            <tr
                                className="cursor-pointer hover:bg-zinc-800/40 focus:outline-none focus-visible:bg-zinc-800/60"
                                key={row.rowFingerprint}
                                tabIndex={0}
                                onClick={() => onSelectRow(row)}
                                onKeyDown={(event) => handleKeyDown(event, row)}
                            >
                                <td className={`${TD} font-medium text-zinc-100`}>{row.entityLabel}</td>
                                <td className={`${TD} text-right text-zinc-200`}>{formatCount(row.supportCount)}</td>
                                <td className={`${TD} text-right`}>
                                    <Signed micros={row.netMeanMicros} />
                                </td>
                                <td className={`${TD} text-right`}>
                                    <Signed className="font-semibold" micros={row.randomEdgeMeanMicros} />
                                </td>
                                {showLift && (
                                    <td className={`${TD} text-right`}>
                                        <Signed micros={row.liftVsTriggerMicros} />
                                    </td>
                                )}
                                <td className={TD}>
                                    <Tooltip content={EVIDENCE_STATE_META[row.evidenceState].description}>
                                        <StatusBadge status={row.evidenceState} tone={EVIDENCE_STATE_META[row.evidenceState].tone}>
                                            {EVIDENCE_STATE_META[row.evidenceState].label}
                                        </StatusBadge>
                                    </Tooltip>
                                </td>
                                <td className={`${TD} text-right`}>
                                    <FaChevronRight aria-hidden="true" className="h-3 w-3 text-zinc-600" />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AlphaAtlasEntityBreakdownView;
